/**
 * PaymentSuccessPage - Post-payment handler
 * User is already logged in via signUp() in SignUpWithPlanForm
 * Just verify session and redirect to app-choice
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"processing" | "success" | "error">(
    "processing"
  );
  const [message, setMessage] = useState("Processing your payment...");
  const sessionId = searchParams.get("session_id");

  console.log('[PaymentSuccessPage] Component mounted');
  console.log('[PaymentSuccessPage] sessionId:', sessionId);

  useEffect(() => {
    if (!sessionId) {
      console.error('[PaymentSuccessPage] No session_id in URL');
      setStatus("error");
      setMessage("No payment session found. Please try again.");
      return;
    }

    const processPayment = async () => {
      try {
        // User should already be logged in from SignUpWithPlanForm
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.error("[PaymentSuccess] No session found - user needs to login");
          setStatus("error");
          setMessage("Payment successful! Please log in to access your account.");
          return;
        }

        console.log("[PaymentSuccess] User authenticated, redirecting to app-choice...");
        setStatus("success");
        setMessage("Payment successful! Redirecting...");
        
        // Redirect to app-choice page
        setTimeout(() => navigate("/app-choice"), 1500);

      } catch (err) {
        console.error("[PaymentSuccess] Error:", err);
        setStatus("error");
        setMessage("An error occurred. Please try logging in manually.");
      }
    };

    // Start processing
    processPayment();
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center flex items-center justify-center">
            {status === "processing" && (
              <>
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                Processing Payment
              </>
            )}
            {status === "success" && (
              <>
                <CheckCircle className="h-6 w-6 text-green-500 mr-2" />
                Payment Successful!
              </>
            )}
            {status === "error" && (
              <>
                <AlertCircle className="h-6 w-6 text-destructive mr-2" />
                Action Required
              </>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">{message}</p>

          {status === "processing" && (
            <div className="flex justify-center">
              <div className="animate-pulse flex space-x-2">
                <div className="h-2 w-2 bg-primary rounded-full"></div>
                <div className="h-2 w-2 bg-primary rounded-full animation-delay-200"></div>
                <div className="h-2 w-2 bg-primary rounded-full animation-delay-400"></div>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-2">
              <Button onClick={() => navigate("/login")} className="w-full">
                Go to Login
              </Button>
              <Button
                onClick={() => navigate("/pricing")}
                variant="outline"
                className="w-full"
              >
                Back to Pricing
              </Button>
            </div>
          )}

          {status === "success" && (
            <p className="text-sm text-muted-foreground">
              Redirecting you now...
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
